import type { CatalogueDto } from '@dbrain/shared';
import { ConfirmDialog, EmptyState, Icons, Menu, MenuItem, Segmented, Skeleton } from '@dbrain/ui';
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.js';
import { keys, useActivity, useCatalogue, useGroups, useSearch } from '../api/hooks.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { PageHeader } from '../components/PageHeader.js';
import { byDay, GroupBar, NewGroupForm, SessionItem, useDebounced, type View } from '../components/sessions/SessionSidebar.js';
import { statusOf } from '../components/status.js';
import { useExperience } from '../experience.js';

/**
 * "Tümünü gör" of the left menu's history (QW-201…QW-204, D-20, D-33):
 * every conversation of this experience by day, the archive and the trash,
 * the groups, a search over what the user and the assistant wrote, and
 * more as the user asks for it.
 */
export function HistoryPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const experience = useExperience();
  const [view, setView] = useState<View>('active');
  const [groupId, setGroupId] = useState<string | null>(null);
  const [addingGroup, setAddingGroup] = useState(false);
  const [emptying, setEmptying] = useState(false);
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim(), 250);
  const catalogue = useCatalogue(experience, view, view === 'active' ? groupId : null);
  const search = useSearch(q, experience);
  const groups = useGroups();
  const activity = useActivity();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: keys.catalogueAll });
    void queryClient.invalidateQueries({ queryKey: ['search'] });
  };
  const emptyTrash = useMutation({
    mutationFn: () => api<{ purged: number }>('POST', '/api/trash/empty'),
    onSuccess: () => {
      setEmptying(false);
      refresh();
    },
  });
  const pages = (catalogue.data as InfiniteData<CatalogueDto> | undefined)?.pages ?? [];
  const pinned = pages[0]?.pinned ?? [];
  const items = pages.flatMap((page) => page.items);
  const groupList = groups.data ?? [];

  return (
    <div className="page page--wide history-page" data-testid="history-page">
      <PageHeader
        title={t('sessions.historyTitle')}
        actions={
          <Menu align="right" label={t('sessions.options')} triggerClassName="ui-icon-button ui-icon-button--outlined" trigger={<Icons.more size={18} />} data-testid="history-options">
            <MenuItem icon={<Icons.plus />} onClick={() => setAddingGroup(true)} data-testid="group-new">
              {t('sessions.newGroup')}
            </MenuItem>
            {view === 'trash' && items.length > 0 ? (
              <MenuItem danger icon={<Icons.trash />} onClick={() => setEmptying(true)} data-testid="trash-empty">
                {t('sessions.emptyTrash')}
              </MenuItem>
            ) : null}
          </Menu>
        }
      />
      <div className="history-page__toolbar">
        <label className="search-pill">
          <Icons.search size={16} />
          <span className="ui-visually-hidden">{t('sessions.search')}</span>
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('sessions.search')} data-testid="session-search" />
        </label>
        {q.length < 2 ? (
          <Segmented
            label={t('sessions.views')}
            value={view}
            onChange={setView}
            options={(['active', 'archived', 'trash'] as const).map((value) => ({ id: value, label: t(`sessions.view.${value}`), 'data-testid': `sessions-view-${value}` }))}
          />
        ) : null}
      </div>
      {addingGroup ? <NewGroupForm onDone={() => setAddingGroup(false)} /> : null}
      {q.length >= 2 ? (
        <ul className="session-list session-list--page" data-testid="search-results">
          {search.isPending ? <Skeleton lines={3} /> : null}
          {search.data?.length === 0 ? <EmptyState icon={<Icons.search />} title={t('sessions.noResults')} /> : null}
          {search.data?.map((hit) => (
            <SessionItem key={hit.conversation.id} row={hit.conversation} view="active" groups={groupList} onChanged={refresh} snippet={hit.snippet} status={statusOf(activity.data, hit.conversation.id)} wide />
          ))}
        </ul>
      ) : (
        <>
          {view === 'active' && groupList.length > 0 ? <GroupBar groups={groupList} groupId={groupId} onSelect={setGroupId} /> : null}
          {view === 'trash' ? <p className="muted">{t('sessions.trashHint')}</p> : null}
          <ErrorAlert error={catalogue.error} />
          {catalogue.isPending ? <Skeleton lines={4} /> : null}
          {pinned.length > 0 ? (
            <section aria-labelledby="history-pinned">
              <h2 id="history-pinned" className="history__day">
                {t('sessions.pinnedTitle')}
              </h2>
              <ul className="session-list session-list--page" data-testid="pinned-list">
                {pinned.map((row) => (
                  <SessionItem key={row.id} row={row} view={view} groups={groupList} onChanged={refresh} status={statusOf(activity.data, row.id)} wide />
                ))}
              </ul>
            </section>
          ) : null}
          <div data-testid="conversation-list">
            {byDay(items).map(({ group, rows }) => (
              <section key={group} aria-labelledby={`history-page-${group}`}>
                <h2 id={`history-page-${group}`} className="history__day">
                  {t(`sessions.day.${group}`)}
                </h2>
                <ul className="session-list session-list--page">
                  {rows.map((row) => (
                    <SessionItem key={row.id} row={row} view={view} groups={groupList} onChanged={refresh} status={view === 'active' ? statusOf(activity.data, row.id) : null} wide />
                  ))}
                </ul>
              </section>
            ))}
          </div>
          {!catalogue.isPending && items.length === 0 && pinned.length === 0 ? <EmptyState icon={<Icons.history />} title={t(`sessions.empty.${view}`)} /> : null}
          {catalogue.hasNextPage ? (
            <button type="button" className="history__more" disabled={catalogue.isFetchingNextPage} onClick={() => void catalogue.fetchNextPage()} data-testid="sessions-more">
              {catalogue.isFetchingNextPage ? t('common.loading') : t('sessions.more')}
            </button>
          ) : null}
        </>
      )}
      {emptying ? (
        <ConfirmDialog
          title={t('sessions.emptyTrash')}
          confirmLabel={t('sessions.emptyTrash')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          busy={emptyTrash.isPending}
          error={<ErrorAlert error={emptyTrash.error} />}
          onConfirm={() => emptyTrash.mutate()}
          onClose={() => setEmptying(false)}
        >
          {t('sessions.emptyTrashConfirm')}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
